import { useCallback } from "react"
import { BadgeTemplate } from "sharedTypes/badge"
import {
  useCanRedo, useCanUndo, useChangeDocument, useDocument, useGetDocument, useRedo, useUndo,
} from "components/CanvasEditor/Editor"
import { infoToast } from "utilities/toasts"
import { keepChangesOnBadge } from "../utils/keepOnBadge"

const tellDoesNotFit = () => infoToast({
  id: "badge-does-not-fit",
  title: "That doesn't fit on the badge",
  description: "Texts, fields and QR codes stay entirely on the badge.",
})

type TemplateProperties = Partial<Pick<
  BadgeTemplate, "name" | "widthMm" | "heightMm" | "printerModel" | "printerMedia"
>>

/** The template being edited, read from and written to the editor's document. */
export const useBadgeTemplate = () => {
  const currentTemplate = useDocument<BadgeTemplate>()
  const getTemplate = useGetDocument<BadgeTemplate>()
  const changeDocument = useChangeDocument<BadgeTemplate>()

  /**
   * Records a new version of the template as one undo step, its changed elements kept on the badge
   * unless they may bleed.
   */
  const pushState = useCallback(
    (next: BadgeTemplate) => changeDocument(
      keepChangesOnBadge(next, getTemplate(), tellDoesNotFit),
    ),
    [changeDocument, getTemplate],
  )

  const handleTemplatePropertyChange = useCallback((updates: TemplateProperties) => {
    pushState({ ...getTemplate(), ...updates, updatedAt: new Date().toISOString() })
  }, [pushState, getTemplate])

  return {
    currentTemplate,
    canUndo: useCanUndo(),
    canRedo: useCanRedo(),
    handleUndo: useUndo(),
    handleRedo: useRedo(),
    handleTemplatePropertyChange,
    pushState,
  }
}

export default useBadgeTemplate
